package com.partejar.app.feature.planos.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.planos.data.Plano
import com.partejar.app.feature.planos.data.PlanoInput
import com.partejar.app.feature.planos.data.PlanosDataSource
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface PlanosUiState {
    data object Loading : PlanosUiState
    data class Content(val plans: List<Plano>) : PlanosUiState
    data class Error(val message: String) : PlanosUiState
}

sealed interface PlanoActionState {
    data object Idle : PlanoActionState
    data object Submitting : PlanoActionState
    data object Success : PlanoActionState
    data class Error(val message: String) : PlanoActionState
}

class PlanosViewModel private constructor(
    private val dataSource: PlanosDataSource,
    private val scopeOverride: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") constructorMarker: Unit
) : ViewModel() {
    constructor(dataSource: PlanosDataSource) : this(dataSource, null, Unit)
    internal constructor(dataSource: PlanosDataSource, scope: CoroutineScope) : this(dataSource, scope, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _uiState = MutableStateFlow<PlanosUiState>(PlanosUiState.Loading)
    val uiState: StateFlow<PlanosUiState> = _uiState.asStateFlow()
    private val _actionState = MutableStateFlow<PlanoActionState>(PlanoActionState.Idle)
    val actionState: StateFlow<PlanoActionState> = _actionState.asStateFlow()

    init { load() }

    fun load() {
        _uiState.value = PlanosUiState.Loading
        scope.launch {
            _uiState.value = try {
                PlanosUiState.Content(dataSource.list())
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                PlanosUiState.Error("Não foi possível carregar os planos.")
            }
        }
    }

    fun create(input: PlanoInput) = submit { dataSource.create(input) }

    fun update(plan: Plano, input: PlanoInput) = submit { dataSource.update(plan.id, input) }

    fun setActive(plan: Plano, active: Boolean) = update(
        plan, PlanoInput(plan.name, plan.currentPrice, active, plan.services)
    )

    fun clearAction() { _actionState.value = PlanoActionState.Idle }

    private fun submit(operation: suspend () -> Plano) {
        if (_actionState.value is PlanoActionState.Submitting) return
        _actionState.value = PlanoActionState.Submitting
        scope.launch {
            _actionState.value = try {
                val saved = operation()
                val current = (_uiState.value as? PlanosUiState.Content)?.plans.orEmpty()
                _uiState.value = PlanosUiState.Content(
                    (current.filterNot { it.id == saved.id } + saved)
                        .sortedWith(compareByDescending<Plano> { it.active }.thenBy { it.name.lowercase() })
                )
                PlanoActionState.Success
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                PlanoActionState.Error("Não foi possível salvar o plano.")
            }
        }
    }
}
